import {
  Alert,
  AlertProps,
  ButtonProps,
  closeCurrentDialog,
  DataStatus,
  KeyboardShortcut,
  openConfirmDialog,
  openDialog,
} from 'platform/components';
import {Box, HStack, Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {always, equals, isNil, not} from 'ramda';
import {isArray, isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {useGetWarehousesQuery} from '@dms/api/warehouse';
import {
  GetMetadaServiceCaseApiResponse,
  PostMaterialBasketItemForPriceListFeApiArg,
  PostMaterialBasketItemForWarehouseApiArg,
  useDeleteAllMaterialBasketItemsMutation,
  useDeleteMaterialBasketItemsMutation,
  useGetMaterialBasketItemsQuery,
  useGetServiceOrderJobQuery,
  useGetServiceOrderQuery,
  useGetServiceOrderVariantQuery,
  usePatchMaterialBasketItemQuantityMutation,
  usePatchOrderItemForMaterialMutation,
  usePostMaterialBasketItemForPriceListMutation,
  usePostMaterialBasketItemForWarehouseMutation,
  usePostMaterialBasketItemsToOrderMutation,
  usePutItemsBasketActionMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {
  AfterSalesMaterialBasket,
  EitherQuantityOrError,
  FullScreenModal,
  handleApiError,
  MaterialBasketItemEditingDetails,
  queryParams,
  useBasketItemsQuantityState,
} from '@dms/shared';
import {
  EditMaterialItem,
  useAfterSalesHeaderParameters,
  useBasketMechanic,
} from '@dms/shared-aftersales';

import {ApiError, buildArray, suffixTestId, TestIdProps, useQueryState} from 'shared/web';

import {focusGridPart} from 'features/eag-grid';

import {useWorkshopUrl} from '../../../../../hooks/useWorkshopUrl';
import {ADD_MATERIAL_GRID_CODE} from '../constants/ADD_MATERIAL_GRID_CODE';
import {MaterialBasketItemType} from '../types/MaterialBasketItemType';
import {ExternalSystemStateType} from '../utils/externalSystemStateType';
import {MaterialListCard} from './MaterialListCard';

interface AddMaterialRequestModalProps extends TestIdProps {
  onSubmit: VoidFunction;
  serviceCase: GetMetadaServiceCaseApiResponse;
}

export function AddMaterialRequestModal(props: AddMaterialRequestModalProps) {
  const {serviceCaseId, orderId, addMaterialRequestJobId} = useWorkshopUrl();
  const [shouldResetSmartSearch, setShouldResetSmartSearch] = useState<boolean>(false);
  const [externalSystemState, setExternalSystemState] = useState<ExternalSystemStateType | null>(
    null
  );
  const [, , closeModal] = useQueryState(queryParams.SERVICE_CASE_ADD_MATERIAL_REQUEST_JOB_ID);

  const {data: serviceJob} = useGetServiceOrderJobQuery({
    serviceCaseId,
    serviceOrderId: orderId,
    serviceJobId: addMaterialRequestJobId,
  });

  const {data: serviceOrder} = useGetServiceOrderQuery({serviceCaseId, serviceOrderId: orderId});

  const headerParameters = useAfterSalesHeaderParameters({
    numberLabel: i18n.t('entity.warehouse.labels.serviceOrder'),
    number: serviceOrder?.number,
    customerLabel: i18n.t('entity.invoice.labels.recipientName'),
    customerId: serviceOrder?.billingInformation?.customerInformation?.customerId,
    vehicleId: props.serviceCase?.vehicleId,
  });

  const {data: orderVariant} = useGetServiceOrderVariantQuery(
    {serviceOrderVariantId: serviceOrder?.serviceOrderVariantId as string},
    {skip: isNilOrEmpty(serviceOrder?.serviceOrderVariantId)}
  );

  const {
    data: availableWarehouses,
    isLoading: isAvailableWarehousesLoading,
    isError: isAvailableWarehousesError,
  } = useGetWarehousesQuery(
    {serviceOrderVariantId: serviceOrder?.serviceOrderVariantId as string},
    {skip: isNilOrEmpty(serviceOrder?.serviceOrderVariantId)}
  );

  const {
    data: basket,
    isLoading: isBasketLoading,
    isFetching: isRefreshingBasket,
    isError: isBasketError,
  } = useGetMaterialBasketItemsQuery({
    serviceCaseId,
    serviceOrderId: orderId,
    serviceJobId: addMaterialRequestJobId,
  });

  const [putItemsBasketAction, {isLoading: isPutItemsLoading}] = usePutItemsBasketActionMutation();

  const [deleteAllMaterialBasketItems, {isLoading: isDiscardingBasket}] =
    useDeleteAllMaterialBasketItemsMutation();

  const [postMaterialBasketItemsToOrder, {isLoading: isCheckingOutBasket}] =
    usePostMaterialBasketItemsToOrderMutation();

  const [deleteMaterialBasketItems, {isLoading: isDeletingBasketItems}] =
    useDeleteMaterialBasketItemsMutation();

  const [patchMaterialBasketItemQuantity, {isLoading: isChangingQuantity}] =
    usePatchMaterialBasketItemQuantityMutation();

  const [, {isLoading: isEditingMaterialItem}] = usePatchOrderItemForMaterialMutation({
    fixedCacheKey: 'edit-service-order-item',
  });

  const [addMaterialBasketItemForWarehouse, warehouseArticleResult] =
    usePostMaterialBasketItemForWarehouseMutation();

  const [addMaterialBasketItemForPriceList, priceListItemResult] =
    usePostMaterialBasketItemForPriceListMutation();

  const basketItems = basket?.materialBasketItem ?? [];

  const isBasketEmpty = isNilOrEmpty(basketItems);

  const isMechanicRequired = basketItems.some((item) => item.requestType === 'dispatch-request');

  const [focusTrigger, setFocusTrigger] = useState(0);

  const {setBasketItemQuantity, hasInvalidBasketItems, hasUnsettledQuantity} =
    useBasketItemsQuantityState(basketItems);

  const {
    mechanics,
    areMechanicsLoading,
    mechanicPayload,
    inputMechanicId,
    mechanicError,
    handleMechanicChange,
    setMechanicError,
  } = useBasketMechanic({
    authorizationProfileId: orderVariant?.authorizationProfileId,
    assignedMechanicId: serviceJob?.assignMechanics?.[0].id,
  });

  const isAddingItemsToBasket = warehouseArticleResult.isLoading || priceListItemResult.isLoading;

  const hasPendingRequest =
    isRefreshingBasket ||
    isAddingItemsToBasket ||
    isDiscardingBasket ||
    isCheckingOutBasket ||
    isChangingQuantity ||
    isEditingMaterialItem ||
    isDeletingBasketItems ||
    isPutItemsLoading;

  const handleCancel = () => {
    setShouldResetSmartSearch(true);
    deleteAllMaterialBasketItems({
      serviceCaseId,
      serviceOrderId: orderId,
      serviceJobId: addMaterialRequestJobId,
    })
      .unwrap()
      .catch(handleApiError)
      .finally(closeModal);
  };

  const handleAddMaterial = () => {
    if (isNil(mechanicPayload) && isMechanicRequired) {
      return setMechanicError(i18n.t('general.notifications.fieldIsRequired'));
    }

    setShouldResetSmartSearch(true);
    postMaterialBasketItemsToOrder({
      serviceCaseId,
      serviceOrderId: orderId,
      serviceJobId: addMaterialRequestJobId,
      body: {
        assignMechanic: mechanicPayload ?? undefined,
      },
    })
      .unwrap()
      .then(() => {
        closeModal();
        props.onSubmit();
      })
      .catch(handleApiError);
  };

  const actions = buildArray<ButtonProps>()
    .add({
      isLoading: isDiscardingBasket,
      isDisabled: hasPendingRequest,
      title: i18n.t('general.actions.discard'),
      variant: 'secondary',
      onClick: handleCancel,
      'data-testid': suffixTestId('cancel', props),
    })
    .add({
      isLoading: isCheckingOutBasket,
      isDisabled: isBasketEmpty || hasPendingRequest || hasUnsettledQuantity,
      title: i18n.t('general.actions.add'),
      keyboardShortcut: KeyboardShortcut.ModEnter,
      onClick: handleAddMaterial,
      'data-testid': suffixTestId('add', props),
    });

  const onUploadClick = () => {
    openConfirmDialog({
      text: i18n.t('entity.addMaterial.lables.uploadFromXentry'),
      onConfirm: () =>
        putItemsBasketAction({
          basketType: 'material-basket',
          body: {actionKey: 'xentry-xhpi-inquiry'},
          serviceCaseId,
          serviceOrderId: orderId,
          serviceJobId: addMaterialRequestJobId,
        })
          .unwrap()
          .then(() => setExternalSystemState('UPLOAD_SCCESS'))
          .catch((error: Error) =>
            handleApiError(error, {callback: () => setExternalSystemState('UPLOAD_ERROR')})
          ),
    });
  };

  // resolves with a success flag so callers can skip their success cleanup when the add failed
  const handleAddWarehouseArticle = async (arg: PostMaterialBasketItemForWarehouseApiArg) =>
    await addMaterialBasketItemForWarehouse(arg)
      .unwrap()
      .then(() => {
        setFocusTrigger((prev) => prev + 1);
        return true;
      })
      .catch((error: ApiError) => {
        handleApiError(error);
        return false;
      });

  const handleAddPriceListItem = async (arg: PostMaterialBasketItemForPriceListFeApiArg) =>
    await addMaterialBasketItemForPriceList(arg)
      .unwrap()
      .then((response) => {
        setFocusTrigger((prev) => prev + 1);
        return response;
      })
      .catch(handleApiError);

  const handleQuantityChange = async (itemId: string, quantity: EitherQuantityOrError) => {
    setBasketItemQuantity(itemId, quantity);

    if (isNil(quantity.newQuantity) || quantity.hasError) {
      return;
    }

    const basketItem = basketItems.find((item) => equals(item.id, itemId));
    const hasQuantityChanged = not(equals(basketItem?.quantity, quantity.newQuantity));

    if (not(hasQuantityChanged)) {
      return;
    }

    await patchMaterialBasketItemQuantity({
      serviceCaseId,
      serviceOrderId: orderId,
      serviceJobId: addMaterialRequestJobId,
      serviceItemId: itemId,
      body: {
        quantity: quantity.newQuantity,
      },
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleDelete = async (itemsIds: string | string[]) => {
    const ids = isArray(itemsIds) ? itemsIds : [itemsIds];

    const requestBody = {serviceItemId: ids};

    await deleteMaterialBasketItems({
      serviceCaseId,
      serviceOrderId: orderId,
      serviceJobId: addMaterialRequestJobId,
      body: requestBody,
    })
      .unwrap()
      .catch(handleApiError);
  };

  const onEdit = (editingDetails: MaterialBasketItemEditingDetails) =>
    openDialog(
      <EditMaterialItem
        itemId={editingDetails.itemId}
        serviceCaseId={serviceCaseId}
        serviceJobId={addMaterialRequestJobId}
        serviceOrderId={orderId}
        onClose={closeCurrentDialog}
        isMechanicEditingDisabled
      />,
      {
        title: `${editingDetails.itemName} (${editingDetails.itemNumber})`,
        withAdditionalFooter: true,
        size: 'large',
      }
    );

  const alertProps = match<ExternalSystemStateType | null, AlertProps>(externalSystemState)
    .with(
      'PREPARED',
      always({
        variant: 'info',
        title: i18n.t('entity.addMaterial.labels.materialPrepared'),
        message: i18n.t('entity.addMaterial.labels.materialPreparedMessage'),
      })
    )
    .with(
      'UPLOAD_SCCESS',
      always({
        variant: 'success',
        title: i18n.t('entity.addMaterial.labels.materialUploaded'),
        onClose: () => setExternalSystemState(null),
      })
    )
    .with(
      'UPLOAD_ERROR',
      always({
        variant: 'error',
        title: i18n.t('entity.addMaterial.labels.materialUploadedError'),
        message: i18n.t('entity.addMaterial.labels.materialUploadedErrorMessage'),
        hyperlinks: [
          {
            leftIcon: 'file/upload',
            onClick: onUploadClick,
            isDisabled: isPutItemsLoading,
            title: i18n.t('general.actions.upload'),
          },
        ],
      })
    )
    .with(null, always({}))
    .exhaustive();

  return (
    <FullScreenModal
      headline={i18n.t('entity.addMaterial.actions.addMaterialRequest')}
      parameters={headerParameters}
      actions={actions}
    >
      <Box padding={4} height="100%">
        <VStack spacing={4} height="100%">
          <Show when={isNotNil(externalSystemState)}>
            <Alert data-testid={suffixTestId('alert', props)} {...alertProps} />
          </Show>
          <Box maxHeight="100%" flex={1}>
            <HStack spacing={4} height="100%">
              <Box flex={5}>
                <DataStatus
                  isLoading={isAvailableWarehousesLoading}
                  isError={isAvailableWarehousesError}
                >
                  <MaterialListCard
                    availableWarehouses={availableWarehouses ?? []}
                    shouldResetSmartSearch={shouldResetSmartSearch}
                    onExternalSystemChange={setExternalSystemState}
                    onWarehouseArticleAdd={handleAddWarehouseArticle}
                    onPriceListItemAdd={handleAddPriceListItem}
                    hasPendingRequest={hasPendingRequest}
                    serviceCase={props.serviceCase}
                    data-testid={suffixTestId('itemList', props)}
                  />
                </DataStatus>
              </Box>
              <Box flex={1} minWidth={86}>
                <AfterSalesMaterialBasket<MaterialBasketItemType>
                  focusTrigger={focusTrigger}
                  basket={{
                    items: basket?.materialBasketItem,
                    totalPrice: basket?.materialBasketTotalPrice,
                    isLoading: isBasketLoading,
                    hasError: isBasketError,
                    hasPendingRequest,
                    hasInvalidItems: hasInvalidBasketItems,
                  }}
                  mechanic={{
                    mechanics,
                    areMechanicsLoading,
                    isMechanicRequired,
                    mechanicError,
                    selectedMechanicId: inputMechanicId,
                    onMechanicChange: handleMechanicChange,
                  }}
                  onQuantityChange={handleQuantityChange}
                  onEdit={onEdit}
                  onDelete={handleDelete}
                  onTabOut={() => focusGridPart(ADD_MATERIAL_GRID_CODE, 'smartSearch')}
                  data-testid={suffixTestId('basket', props)}
                />
              </Box>
            </HStack>
          </Box>
        </VStack>
      </Box>
    </FullScreenModal>
  );
}
